// Stage 48: per-row edit modal. Pre-filled from the existing row; same
// fields as the new-connection wizard but no token mint / display.

import { useState } from 'react';
import {
  ALL_SCOPES,
  updateConnection,
  type ConnectionListRow,
  type EditConnectionInput,
  type Scope,
} from '../api/connections';

interface Props {
  initial: ConnectionListRow;
  onClose: () => void;
  onSaved: () => void;
}

function unixToDatetimeLocal(unix: number | null): string {
  if (unix == null) return '';
  const d = new Date(unix * 1000);
  // datetime-local expects YYYY-MM-DDTHH:MM in local time.
  const pad = (n: number) => n.toString().padStart(2, '0');
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}

export function EditConnectionModal({ initial, onClose, onSaved }: Props) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const [label, setLabel] = useState(initial.label);
  const [scopes, setScopes] = useState<Scope[]>(initial.scopes);
  const [origin, setOrigin] = useState(initial.allowed_origin);
  const [rpm, setRpm] = useState<string>(
    String(initial.rate_limit_per_minute),
  );
  const [expiresAt, setExpiresAt] = useState<string>(
    unixToDatetimeLocal(initial.expires_at),
  );
  const [notes, setNotes] = useState(initial.notes ?? '');
  const [enabled, setEnabled] = useState<boolean>(initial.enabled);

  function toggleScope(s: Scope, on: boolean): void {
    setScopes((prev) =>
      on ? [...new Set([...prev, s])] : prev.filter((x) => x !== s),
    );
  }

  async function save(): Promise<void> {
    setErr(null);

    if (!label.trim()) return setErr('Label is required.');
    if (scopes.length === 0) return setErr('Pick at least one scope.');
    if (!origin.trim()) return setErr('Allowed origin is required.');

    let rpmNum: number;
    {
      const n = parseInt(rpm, 10);
      if (!Number.isFinite(n) || n < 1 || n > 6000) {
        return setErr('Rate limit must be a number between 1 and 6000.');
      }
      rpmNum = n;
    }

    let expiresAtUnix: number | null = null;
    if (expiresAt) {
      const d = new Date(expiresAt);
      if (Number.isNaN(d.getTime())) return setErr('Invalid expiry date.');
      expiresAtUnix = Math.floor(d.getTime() / 1000);
    }

    const patch: EditConnectionInput = {
      label: label.trim(),
      scopes,
      allowed_origin: origin.trim(),
      rate_limit_per_minute: rpmNum,
      expires_at: expiresAtUnix,
      notes: notes.trim() || null,
      enabled,
    };

    setBusy(true);
    try {
      await updateConnection(initial.id, patch);
      onSaved();
      onClose();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="modal-backdrop"
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="modal modal-settings">
        <header className="modal-header">
          <h2>Edit connection</h2>
          <button
            className="modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </header>
        <section className="modal-body">
          <div className="panel">
            <label className="voice-toggle-row">
              <input
                type="checkbox"
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
                disabled={busy}
              />
              <span>Enabled</span>
            </label>

            <label className="form-label">
              Label
              <input
                type="text"
                maxLength={80}
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                disabled={busy}
              />
            </label>

            <fieldset className="form-fieldset">
              <legend>Allowed scopes</legend>
              <div className="scope-grid">
                {ALL_SCOPES.map((s) => (
                  <label key={s} className="voice-toggle-row">
                    <input
                      type="checkbox"
                      checked={scopes.includes(s)}
                      onChange={(e) => toggleScope(s, e.target.checked)}
                      disabled={busy}
                    />
                    <span>{s}</span>
                  </label>
                ))}
              </div>
            </fieldset>

            <label className="form-label">
              Allowed origin
              <input
                type="text"
                value={origin}
                onChange={(e) => setOrigin(e.target.value)}
                disabled={busy}
                autoComplete="off"
                spellCheck={false}
              />
            </label>

            <label className="form-label">
              Rate limit (requests / minute)
              <input
                type="number"
                min={1}
                max={6000}
                step={1}
                value={rpm}
                onChange={(e) => setRpm(e.target.value)}
                disabled={busy}
              />
            </label>

            <label className="form-label">
              Expires
              <input
                type="datetime-local"
                value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
                disabled={busy}
              />
              <span className="muted small">
                Clear to remove the expiry.
              </span>
            </label>

            <label className="form-label">
              Notes
              <textarea
                rows={3}
                maxLength={2000}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                disabled={busy}
              />
            </label>

            {err ? (
              <div className="status-missing">Error: {err}</div>
            ) : null}
          </div>
        </section>
        <footer className="modal-footer">
          <button onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            className="btn-primary"
            onClick={() => void save()}
            disabled={busy}
          >
            {busy ? 'Saving…' : 'Save changes'}
          </button>
        </footer>
      </div>
    </div>
  );
}
